import { CircularProgress, Skeleton } from "@wealthx/ui";
import React, { useState, useMemo } from "react";

import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import classNames from "classnames";
import {
  useConversationList,
  useDeleteConversation,
  useUpdateConversationTitle,
} from "../../../hooks/conversation.hooks";
import ConversationListItem from "./conversation-list-item";
import "./conversation-list.scss";

interface ConversationListProps {
  isVisible: boolean;
  activeConversationID: string | null;
  agencyId?: string;
  onSelectConversation: (conversationID: string) => void;
  onDeleteActiveConversation?: () => void;
}

const ConversationList: React.FC<ConversationListProps> = ({
  isVisible,
  activeConversationID,
  agencyId,
  onSelectConversation,
  onDeleteActiveConversation,
}) => {
  const [expanded, setExpanded] = useState(true);

  const { conversations, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useConversationList(agencyId, isVisible);

  const deleteMutation = useDeleteConversation(agencyId);
  const renameMutation = useUpdateConversationTitle(agencyId);

  const handleDelete = useMemo(
    () => (conversationID: string) => {
      deleteMutation.mutate(conversationID);
      if (conversationID === activeConversationID && onDeleteActiveConversation) {
        onDeleteActiveConversation();
      }
    },
    [deleteMutation, activeConversationID, onDeleteActiveConversation]
  );

  const handleRename = useMemo(
    () => (conversationID: string, title: string) => {
      renameMutation.mutate({ conversationID, title });
    },
    [renameMutation]
  );

  return (
    <div className="conversation-list">
      <button
        type="button"
        className="collapsible-menu__section-header"
        onClick={() => setExpanded(!expanded)}
        style={{ cursor: "pointer" }}
        aria-expanded={expanded}
        aria-controls="conversation-list-section"
      >
        <ChatBubbleIcon className="collapsible-menu__section-icon" sx={{ color: "white" }} />
        <span className="collapsible-menu__section-title">Chats</span>
      </button>

      <div
        id="conversation-list-section"
        className={classNames("conversation-list__container", {
          "conversation-list__container--collapsed": !expanded,
        })}
      >
        {isLoading && (
          <div className="conversation-list__skeleton-group">
            {[1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={32}
                sx={{
                  bgcolor: "rgba(255, 255, 255, 0.08)",
                  borderRadius: "4px",
                  marginBottom: "4px",
                }}
              />
            ))}
          </div>
        )}

        {!isLoading && conversations.length === 0 && (
          <div className="conversation-list__empty">No conversations yet</div>
        )}

        {!isLoading &&
          conversations.map((conv) => (
            <ConversationListItem
              key={conv.conversationID}
              conversation={conv}
              isActive={conv.conversationID === activeConversationID}
              onSelect={onSelectConversation}
              onDelete={handleDelete}
              onRename={handleRename}
            />
          ))}

        {hasNextPage && (
          <button
            type="button"
            className="conversation-list__load-more"
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? (
              <CircularProgress size={14} sx={{ color: "rgba(255, 255, 255, 0.7)" }} />
            ) : (
              "Load more"
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default ConversationList;
